import React from 'react';
import InterviewCareGiverSection from '../../components/others/interview/InterviewCareGiverSection';
import InterviewFamilySection from '../../components/others/interview/InterviewFamilySection';
import { Divider } from '@nextui-org/react';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../store/reducer-main';
import { IExecuteCompatibilityCalculationPayload } from '../../../types/cloudFunctions';
import { CareGiver, CaregiverNationality } from '../../../types/caregivers';
import { Family } from '../../../types/families';
import { calculateCompatibility, calculateCompatibilitySuccess, loadInterviewCareGiver, loadInterviewFamily, resetInterviewPage, updateDistanceFromFamily } from '../../../store/interview/slice';
import { useLocation, useNavigate } from 'react-router';
import theme from '../../theme';
import PageLayout from '../../components/common/PageLayout';
import InterviewResultHeader from '../../components/others/interview/InterviewResultHeader';
import Typography from '../../components/common/Typography';
import { assignCaregiverToFamily } from '../../../service/families';
import TextButton from '../../components/common/TextButton';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { createPdfToExport } from '../../../helpers/utils';
import { toast } from 'react-toastify';
import { TraitData } from '../../../types/common';

export default function InterviewPage() {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { userProfile } = useSelector((state: StoreState) => state.profile);
  const { family, caregiver, calculationInProgress, calculationResult, distanceFromFamily } = useSelector((state: StoreState) => state.interview);

  const [assignLoading, setAssignLoading] = React.useState<boolean>(false);

  console.log('location state', location.state);

  React.useEffect(() => {
    if (!location.state) {
      dispatch(resetInterviewPage());
    } else {
      const { calculation: c } = location.state;
      console.log(c);
      dispatch(calculateCompatibilitySuccess(c));
      const {} = c;
      dispatch(loadInterviewFamily(c.relativeFamilyData));
      dispatch(loadInterviewCareGiver(c.relativeCaregiverData));
      dispatch(updateDistanceFromFamily(c.distance));
    }
  }, []);

  const interviewInProgress = React.useMemo(() => {
    return !family; // se è valorizzata la famiglia allora il questionario è iniziato
  }, [family]);

  const assignDisabled = React.useMemo(() => {
    if (assignLoading) return true;
    if (!family || !caregiver) return true;
    if (family.assignedCaregiver || caregiver.assignedTo) return true;
    return false;
  }, [family, caregiver, assignLoading]);

  const calculateDisabled = React.useMemo(() => {
    if (!caregiver) return true;
    if (!family) return true;
    if (!distanceFromFamily) return true;
    return false;
  }, [family, caregiver, distanceFromFamily]);

  const constructCalculationData = (
    caregiver: CareGiver,
    family: Family,
  ): IExecuteCompatibilityCalculationPayload => {
    const commonTraits: TraitData[] = [];
    for (let t of family.traits) {
      const idx = caregiver.traits.findIndex(el => el.trait === t.trait);
      commonTraits.push({trait: t.trait, on: idx < 0 ? false : caregiver.traits[idx].on});
    }
    return {
      dateOfBirth: caregiver.personalData.tsDateOfBirth,
      distanceFromFamily: parseInt(distanceFromFamily || '0'),
      commonTraits: commonTraits,
      familyExcludedNationality: family.excludedNationalities as CaregiverNationality[],
      caregiverNationality: caregiver.personalData.nationality,
      languageKnowledge: caregiver.languageKnowledge,
      coockingKnowledge: caregiver.coockingKnowledge,
      personalities: caregiver.personalities,
      nightTime: caregiver.nightTime,
      weekendAvailability: caregiver.weekendAvailability,
      smokes: caregiver.smokes,
      otherPeoplePresence: caregiver.otherPeoplePresence,
      experience: String(caregiver.yearsOfExperience),
      references: caregiver.personalData.references,
      caregiverPersonalData: caregiver.personalData,
      familyPersonalData: family.personalData,
      caregiverId: caregiver.id,
      familyId: family.id,
      familyData: family,
      caregiverData: caregiver,
    }
  }

  const assignCaregiver = async () => {
    try {
      if (!family || !caregiver || !userProfile) return;
      if (family.assignedCaregiver) {
        toast.error('La famiglia ha già una badante assegnata')
        return;
      } else if (caregiver.assignedTo) {
        toast.error('La badante è già assegnata ad una famiglia');
        return;
      } else {
        setAssignLoading(true);
        await assignCaregiverToFamily(userProfile.uid, family.id, caregiver.id);
        const newSelectedFamily: Family = { ...family, assignedCaregiver: caregiver.id };
        dispatch(loadInterviewFamily(newSelectedFamily));
        toast.success('Badante assegnata con successo');
        setAssignLoading(false);
      }
    } catch (error) {
      setAssignLoading(false);
      console.log('err', error);
      toast.error('Si è verificato un errore');
    }
  }

  // TODO: FARE CON UNA ACTION SAGA + Gestione errori
  const onSubmit = async() => {
    try {
      if (!caregiver || !family) return;
      const calculationData = constructCalculationData(caregiver, family);
      console.log(calculationData);
      dispatch(calculateCompatibility(calculationData));
    } catch (error) {
      console.log('CALCULATION ERROR', error);
    }
  }

  const handleGeneratePDF = async () => {
    try {
      if(calculationResult && userProfile){
        await createPdfToExport(userProfile.uid, calculationResult);
        toast.success("PDF esportato con successo")
      }else toast.error("Errore nell'export del PDF. Per favore rirpova")
    } catch (error) {
      toast.error('Si è verificato un errore durante la generazione del report');
      console.log('err pdf', error);
    }
  }


  const gotoHistoryPage = () => {
    navigate('/history');
  }

  return (
    <PageLayout nestedPage>
      <div className='flex justify-between items-center'>
        <div className='flex flex-col'>
          <Typography variant='headline3'>
            Colloqui con Badanti
          </Typography>
          <Typography className='mt-2'>
            Effettua il colloquio e calcola la compatibilità con la famiglia
          </Typography>
        </div>
        <div className='flex items-center'>
        <TextButton
          icon={<HistoryRoundedIcon style={{color: theme.color.primary}} />}
          onPress={() => navigate('/history')}
          title='STORICO COLLOQUI'/>
        </div>
      </div>
      <InterviewResultHeader 
        style={{marginTop: theme.sizing.x3}} 
        calculate={onSubmit} 
        calculateDisabled={calculateDisabled} 
        loading={calculationInProgress} 
        calculationResult={calculationResult} 
        onAssign={assignCaregiver} 
        onDownloadReport={handleGeneratePDF}
        downloadDisabled={!calculationResult}
        assignDisabled={assignDisabled} />
      <div className='flex w-full h-full mt-6' style={{
        borderRadius: theme.sizing.x2,
        backgroundColor: theme.color.background,
        border: '1.5px solid lightgray'
      }}>
        <div className='flex flex-1 py-8 pl-8 pr-4'>
          <InterviewFamilySection />
        </div>
        <Divider orientation="vertical" />
        <div className='flex flex-1 py-8 pr-8 pl-4'>
          <InterviewCareGiverSection />
        </div>
      </div>
    </PageLayout>
  )
}